import ReactDOM from 'react-dom';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import ModalBase from './modals/modal_base';

export type Options = {
  modalClassName?: string;
  modalWrapperClass?: string;
  disableModalContentClass?: boolean;
  disableBgClick?: boolean;
  center?: boolean;
  fullHeight?: boolean;
  afterClose?: () => void;
};
type ShowModalProps<ModalClassType extends React.ElementType> = Omit<
  React.ComponentProps<ModalClassType>,
  // the types here are a bit limited. They won't capture mistakes made in these props
  'modalId' | 'closeModal' | 'addWillCloseListener'
>;

// Returns only those keys that undefined can't be assigned to
type RequiredKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? never : K;
}[keyof T];

// T is the type needed for the props and is optional iff T has no required properties
type PotentialShowModalArguments<T> =
  RequiredKeys<T> extends never
    ? [T?, Options?, (() => void)?]
    : [T, Options?, (() => void)?];

/**
 * @deprecated Prefer using `Modal.tsx` or creating a portal-based component.
 * This function renders modals outside of the normal rendering flow of React,
 * which makes propagating state changes and handling user input difficult.
 */
export default function showModal<T extends React.ElementType>(
  modalClass: T,
  ...[
    // this weird format is needed to enable props to be conditionally optional
    props,
    options,
    callback,
  ]: PotentialShowModalArguments<ShowModalProps<T>>
) {
  const modalId = 'js-bootstrap-modal-' + uuidv4();
  const modalViewParams = { modalId: modalId, ...props };
  const container = document.createElement('div');
  const body = document.body;

  // Fullscreen:
  // const container = document.createElement("div");
  // container.style.cssText = "width: 100%; height: 100%; display: inline; overflow: scroll";

  const destroy = function () {
    // Not sure if this is the best way to remove and cleanup the modal.
    // Not sure if we want to remove in every case.
    // This behavior likely needs to be more elaborate, but for now
    // We only have a use case where we need to remove modals completely,
    // And this seems to work
    // see also: http://stackoverflow.com/questions/13177426/how-to-destroy-bootstrap-modal-window-completely/18169689#18169689
    // - Alexey

    ReactDOM.unmountComponentAtNode(container);
    body.removeChild(container);
    if (options && options.afterClose) {
      options.afterClose();
    }
  };

  const baseOptions = {
    modalId: modalId,
    modalViewClass: modalClass,
    modalViewParams: modalViewParams,
    onClose: destroy,
    modalClassName: '',
    disableModalContentClass: false,
    disableBgClick: false,
    center: false,
    fullHeight: false,
    ...options,
  };

  const modalViewBase = <ModalBase {...baseOptions} />;

  body.appendChild(container);
  // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
  // eslint-disable-next-line react/no-render-return-value
  return ReactDOM.render(modalViewBase, container, callback);
}
